import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../buttons/IconButton';
import ProfilerHeatmap from '../ProfilerHeatmap/ProfilerHeatmap';
import HeatmapLegend from '../HeatmapLegend/HeatmapLegend';
import ProfilerHeatmapControls from './ProfilerHeatmapControls';

/**
 * The Profiler "heatmap" section of a results surface: a header carrying the
 * tile-size / colour-coding / grouping controls and a collapse toggle, then the
 * ProfilerHeatmap and its legend.
 *
 * TD-2720 introduced it on the search-results page, where the heatmap sits *above*
 * the list rather than replacing it. That gave the section three properties worth
 * restating, because they are the reasons it exists at all:
 *
 * 1. The controls live here, directly above the tiles they drive. They used to be
 *    split between a page's sticky results header (desktop) and a mobile-only copy.
 * 2. It collapses, borrowing the control from the Projects "Search criteria" pattern
 *    (`ProfilerProjectView.jsx`) — borderless chevron, right-aligned, body rendered
 *    conditionally — without that pattern's section title, which the heatmap does not
 *    need: the tiles identify themselves. Collapsing hands the list the whole viewport
 *    without leaving the view and without losing the metric selections. Persisting the
 *    collapsed state is the caller's job.
 * 3. It always passes `allowPageScroll` to ProfilerHeatmap. That is not a caller
 *    choice: rendering this section *means* a list continues below the tiles, so a
 *    plain wheel or one-finger swipe has to reach it. Zoom moves to Ctrl/Cmd+wheel
 *    and pinch — otherwise scrolling down over the heatmap just zooms and the list
 *    stays unreachable by the one gesture everyone tries first.
 *
 * TD-2733 — moved here from `pages/search/SearchResults/components/ProfilerHeatmapView`
 * so the saved-project and team-feedback surfaces get the same layout instead of a
 * third copy of it. The signature changed with the move: everything that genuinely
 * differs per surface is an explicit prop, because the surfaces disagree on the
 * result array, on how `showCagr4Y` is derived, and on whether per-row exclusion
 * exists at all (team feedback has none). Only the metric/menu state — identical
 * everywhere — is passed as one `controls` bag.
 *
 * `controls` is duck-typed rather than typed member-by-member because it is either a
 * MobX store (search, saved projects) or the return value of `useProfilerViewControls`
 * (team feedback). Callers passing a plain object must build it fresh on every render:
 * `observer()` wraps this component in `React.memo`, so a stabilised object that is
 * later mutated would be skipped and the controls would freeze.
 */
const ProfilerHeatmapSection = observer(
  ({
    controls,
    results,
    searchId,
    searchCategory,
    groupProperty,
    layout,
    isCollapsed,
    onToggleCollapse,
    isLoading,
    hasResults,
    heatmapKey,
    showCagr4Y,
    showEbitMargin,
    displayDetails,
    getIsExcludingResult,
    excludeResult,
    // ProfilerHeatmap calls this in a mount effect without checking, so the default keeps
    // the prop genuinely optional as its propTypes claim.
    setToggleProfilerHeatmapGroupingCallback = () => {}
  }) => {
    const isMobile = layout === 'mobile';

    // Surfaces that feed the tiles a *filtered* set introduced an input the heatmap
    // never used to get: zero companies, when the active filters match nothing.
    // ProfilerHeatmap does not defend against that — its treemap root has no
    // children, so `root.forEach` classifies the root itself as a leaf and paints one
    // full-size tile with a NaN size. Skip the tiles instead; the list right below
    // already says why it is empty.
    const hasVisibleCompanies = results.length > 0;

    return (
      <div className="profiler-heatmap-section">
        <div className="profiler-heatmap-section-header">
          {/* Desktop keeps the controls on this row; mobile wraps them onto their own
              rows below, where `.mobile-view` is measured into the page's
              heatmap-controls height variable. */}
          {!isMobile && !isCollapsed && (
            <ProfilerHeatmapControls
              controls={controls}
              isLoading={isLoading}
              hasResults={hasResults}
              layout="desktop"
            />
          )}
          <IconButton
            id="profiler-heatmap-collapse-toggle"
            type={ICON_BUTTON_TYPES.DEFAULT}
            wrapClassName="profiler-heatmap-collapse-toggle"
            icon={isCollapsed ? ICON_BUTTON_ICONS.CHEVRON_DOWN : ICON_BUTTON_ICONS.CHEVRON_UP}
            innerText={isCollapsed ? 'Expand heatmap' : 'Collapse'}
            iconOnRight
            withBorder={false}
            onClick={onToggleCollapse}
          />
        </div>

        {isMobile && !isCollapsed && (
          <ProfilerHeatmapControls
            controls={controls}
            isLoading={isLoading}
            hasResults={hasResults}
            layout="mobile"
          />
        )}

        {!isCollapsed && hasVisibleCompanies && (
          <>
            <ProfilerHeatmap
              key={heatmapKey}
              results={results}
              displayDetails={displayDetails}
              searchId={searchId}
              sizeProperty={controls.profilerHeatmapTileSize}
              colorProperty={controls.profilerHeatmapColorCoding}
              groupProperty={groupProperty}
              setToggleProfilerHeatmapGroupingCallback={setToggleProfilerHeatmapGroupingCallback}
              showCagr4Y={showCagr4Y}
              searchCategory={searchCategory}
              getIsExcludingResult={getIsExcludingResult}
              excludeResult={excludeResult}
              showEbitMargin={showEbitMargin}
              allowPageScroll
            />
            <HeatmapLegend colorCoding={controls.profilerHeatmapColorCoding} />
          </>
        )}
      </div>
    );
  }
);

ProfilerHeatmapSection.propTypes = {
  controls: PropTypes.object.isRequired,
  results: PropTypes.array.isRequired,
  searchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  searchCategory: PropTypes.string,
  groupProperty: PropTypes.string,
  layout: PropTypes.oneOf(['desktop', 'mobile']),
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  hasResults: PropTypes.bool,
  heatmapKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showCagr4Y: PropTypes.bool,
  showEbitMargin: PropTypes.bool,
  displayDetails: PropTypes.func,
  getIsExcludingResult: PropTypes.func,
  excludeResult: PropTypes.func,
  setToggleProfilerHeatmapGroupingCallback: PropTypes.func
};

export default ProfilerHeatmapSection;
